import React from 'react';
import { Navigate, RouterProvider } from 'react-router-dom';
import { AnonymousRoute, ProtectedPage, ProtectedRoute } from '@core/Auth/components';

import { Login } from 'AuthPart/Login';
import { ForgotPassword } from 'AuthPart/ForgotPassword';
import { SetPassword } from 'AuthPart/SetPassword';
import { DevicePairing, ManagedDevicesPage } from 'AuthPart/DevicePairing';
import { CompaniesPage } from 'Companies/components';
import { UsersPage } from 'Users/components';
import { NeoAnalyticsPage } from 'NeoAnalytics/components';
import { GroupsPage } from 'Groups/components';
import { ProceduresPage } from 'Procedures/components';
import { AnalyticsPage } from 'Analytics/components/AnalyticsPage';
import { LegacyAnalyticsPage } from 'Analytics/components/LegacyAnalyticsPage';
import { UserAnalyticsPage } from 'Analytics/components/UserAnalyticsPage';
import { ProfilePage } from 'Profile/components/ProfilePage';

import { RoutePaths } from './RoutePaths';
import { PagesNames } from './PagesNames';
import { AppExternalLayout, AppLayout } from '../layouts';
import { LicensesPage } from 'Licenses/LicensesPage';
import { DownloadPage } from 'Download/DownloadPage';
import { ProcedureAnalyticsPage } from 'NeoAnalytics/components/ProcedureAnalyticsPage';
import { ProcedureSummaryPage } from 'NeoAnalytics/components/ProcedureSummaryPage';
import { UserSummaryPage } from 'NeoAnalytics/components/UserSummaryPage';

export const routes = [
    {
        path: RoutePaths.root, // This is '/'
        children: [
            {
                element: <AnonymousRoute />,
                children: [
                    {
                        path: RoutePaths.login,
                        element: <AppExternalLayout><Login /></AppExternalLayout>,
                    },
                    {
                        path: RoutePaths.register,
                        element: <AppExternalLayout><Login isRegister /></AppExternalLayout>,
                    },
                    {
                        path: RoutePaths.forgotPassword,
                        element: <AppExternalLayout><ForgotPassword /></AppExternalLayout>,
                    },
                    {
                        path: RoutePaths.setPassword,
                        element: <AppExternalLayout><SetPassword /></AppExternalLayout>,
                    },
                ],
            },
            {
                element: <ProtectedRoute />,
                children: [
                    {
                        index: true,
                        element: <Navigate to={RoutePaths.users} />,
                    },
                    {
                        path: RoutePaths.companies,
                        element: (
                            <ProtectedPage page={PagesNames.Companies}>
                                <AppLayout path={RoutePaths.companies} showSidebar={false}><CompaniesPage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.users,
                        element: (
                            <ProtectedPage page={PagesNames.Users}>
                                <AppLayout path={RoutePaths.users} showSidebar={false}><UsersPage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.usersNeoAnalytics,
                        element: (
                            <ProtectedPage page={PagesNames.Users}>
                                <AppLayout path={RoutePaths.usersNeoAnalytics} showSidebar={false}><NeoAnalyticsPage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.usersNeoAnalyticsExecution,
                        element: (
                            <ProtectedPage page={PagesNames.Users}>
                                <AppLayout path={RoutePaths.usersNeoAnalytics} showSidebar={false}><NeoAnalyticsPage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.userSummary,
                        element: (
                            <ProtectedPage page={PagesNames.Users}>
                                <AppLayout path={RoutePaths.usersNeoAnalytics} showSidebar={false}><UserSummaryPage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.groups,
                        element: (
                            <ProtectedPage page={PagesNames.Groups}>
                                <AppLayout path={RoutePaths.groups} showSidebar={false}><GroupsPage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.groupNeoAnalytics,
                        element: (
                            <ProtectedPage page={PagesNames.Groups}>
                                <AppLayout path={RoutePaths.groupNeoAnalytics} showSidebar={false}><NeoAnalyticsPage fromGroup={true}/></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.groupNeoAnalyticsExecution,
                        element: (
                            <ProtectedPage page={PagesNames.Groups}>
                                <AppLayout path={RoutePaths.groupNeoAnalytics} showSidebar={false}><NeoAnalyticsPage fromGroup={true}/></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.procedures,
                        element: (
                            <ProtectedPage page={PagesNames.Procedures}>
                                <AppLayout path={RoutePaths.procedures} showSidebar={false}><ProceduresPage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.proceduresNeoAnalytics,
                        element: (
                            <ProtectedPage page={PagesNames.Procedures}>
                                <AppLayout path={RoutePaths.proceduresNeoAnalytics} showSidebar={false}><ProcedureAnalyticsPage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.proceduresNeoAnalyticsExecution,
                        element: (
                            <ProtectedPage page={PagesNames.Procedures}>
                                <AppLayout path={RoutePaths.proceduresNeoAnalytics} showSidebar={false}><ProcedureAnalyticsPage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.procedureSummary,
                        element: (
                            <ProtectedPage page={PagesNames.Procedures}>
                                <AppLayout path={RoutePaths.proceduresNeoAnalytics} showSidebar={false}><ProcedureSummaryPage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.analytics,
                        element: (
                            <ProtectedPage page={PagesNames.Analytics}>
                                <AppLayout path={RoutePaths.analytics} showSidebar={false}><AnalyticsPage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.analyticsLegacy,
                        element: (
                            <ProtectedPage page={PagesNames.Analytics}>
                                <AppLayout path={RoutePaths.analyticsLegacy}><LegacyAnalyticsPage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.analyticsByUser,
                        element: (
                            <ProtectedPage page={PagesNames.Analytics}>
                                <AppLayout path={RoutePaths.analyticsByUser}><UserAnalyticsPage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.licenses,
                        element: (
                            <ProtectedPage page={PagesNames.Licenses}>
                                <AppLayout path={RoutePaths.licenses} showSidebar={false}><LicensesPage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.download,
                        element: (
                            <ProtectedPage page={PagesNames.Download}>
                                <AppLayout path={RoutePaths.download} showSidebar={false}><DownloadPage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        path: RoutePaths.profile,
                        element: (
                            <ProtectedPage page={PagesNames.Profile}>
                                <AppLayout path={RoutePaths.profile} showSidebar={false}><ProfilePage /></AppLayout>
                            </ProtectedPage>
                        ),
                    },
                    {
                        // No ProtectedPage wrapper: approving a pairing is not a permissioned area,
                        // it is something any signed-in user does for their own headset. The header
                        // stays visible on purpose, so they can see which account they are approving as.
                        path: RoutePaths.pair,
                        element: <AppLayout path={RoutePaths.pair} showSidebar={false}><DevicePairing /></AppLayout>,
                    },
                    {
                        // Gated by role inside the page rather than by ProtectedPage: page
                        // permissions are a different axis from roles, and there is no PagesNames
                        // entry for a view that lives off the profile menu.
                        path: RoutePaths.pairedDevices,
                        element: (
                            <AppLayout path={RoutePaths.pairedDevices} showSidebar={false}>
                                <ManagedDevicesPage />
                            </AppLayout>
                        ),
                    },
                ],
            },
        ],
    },
];

export const AppRouter = ({router} : any) => {
    return (<RouterProvider router={router} fallbackElement={"Loading Manager..."} />);
};
